import {
  Drawer,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  ImageList,
  ImageListItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import FormWrapper from "../Form/FormWrapper.js";
import { useDropzone } from "react-dropzone";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { ProductImageCropper } from "../Products/ImageCropper";
import { createInfographic, updateInfographic } from "../../apis/Playoff.js";

export default function InfographicDialog({
  open,
  onClose,
  playoffId,
  infographic,
  onSuccess,
}) {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      description: "",
    },
  });

  const [galleryImages, setGalleryImages] = useState([]);
  const [imageError, setImageError] = useState("");
  const [cropperOpen, setCropperOpen] = useState(false);

  const [selectedImage, setSelectedImage] = useState(null);

  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  useEffect(() => {
    if (!infographic) {
      setValue("description", "");
      setGalleryImages([]);
      return;
    }

    setValue("description", infographic.description);

    setGalleryImages(
      (infographic.images || []).map((img) => ({
        id: img.id,
        file: img.file,
        isExisting: true,
        deleted: false,
      })),
    );
  }, [infographic, setValue]);
  useEffect(() => {
    if (!open) {
      reset({ description: "" });
      setGalleryImages([]);
      setSelectedImage(null);
      setSelectedImageIndex(null);
      setCropperOpen(false);
    }
  }, [open, reset]);

  const onDrop = (acceptedFiles) => {
    const imageFiles = acceptedFiles.filter((file) =>
      file.type.startsWith("image/"),
    );

    if (!imageFiles.length) {return;}
    setImageError("");
    setGalleryImages((prev) => [
      ...prev,
      ...imageFiles.map((file) => ({
        id: null,
        file,
        isExisting: false,
        deleted: false,
      })),
    ]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/*": [],
    },
    multiple: true,
    onDrop,
  });

  const openCropper = (image, index) => {
    setSelectedImageIndex(index);

    if (image.file instanceof File) {
      setSelectedImage(URL.createObjectURL(image.file));
    } else {
      const urlObj = new URL(image.file);

      const isCloudFront = urlObj.hostname === "d2h50zujfkj84t.cloudfront.net";

      const baseURL = process.env.REACT_APP_BASE_URL;

      const finalImage = isCloudFront
        ? `${baseURL}/web/proxy-image/?url=${encodeURIComponent(image.file)}`
        : image.file;

      setSelectedImage(finalImage);
    }

    setCropperOpen(true);
  };

  const handleDeleteImage = (index) => {
    setGalleryImages((prev) => {
      const updated = prev.filter((_, i) => i !== index);

      if (updated.length === 0) {
        setImageError("Please upload at least one image.");
      }

      return updated;
    });
  };

  const handleCroppedImage = async (croppedImage) => {
    let file = croppedImage;

    if (typeof croppedImage === "string") {
      const blob = await fetch(croppedImage).then((r) => r.blob());

      file = new File([blob], `cropped-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });
    }

    setGalleryImages((prev) =>
      prev.map((img, index) =>
        index === selectedImageIndex
          ? {
            ...img,
            file,
            isExisting: false,
          }
          : img,
      ),
    );

    setCropperOpen(false);
    setSelectedImage(null);
    setSelectedImageIndex(null);
  };

  const onSubmit = async (data) => {
    if (galleryImages.length === 0) {
      setImageError("Please upload at least one image.");
      return;
    }

    setImageError("");
    try {
      const formData = new FormData();

      formData.append("playoff", playoffId);
      formData.append("description", data.description);

      for (const image of galleryImages) {
        if (image.file instanceof File) {
          formData.append("uploaded_images", image.file);
        } else {
          const finalImage = `${process.env.REACT_APP_BASE_URL}/web/proxy-image/?url=${encodeURIComponent(
            image.file,
          )}`;

          const blob = await fetch(finalImage).then((r) => r.blob());

          const file = new File([blob], `image-${Date.now()}.jpg`, {
            type: blob.type || "image/jpeg",
          });

          formData.append("uploaded_images", file);
        }
      }
      if (infographic) {
        await updateInfographic(infographic.id, formData);
      } else {
        await createInfographic(formData);
      }

      await onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
    }
  };
  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose}>
        <Box
          sx={{
            width: 600,
            p: 1,
            height: "100vh",
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <IconButton sx={{ position: "fixed" }} onClick={onClose}>
            <CloseIcon />
          </IconButton>

          <FormWrapper onSubmit={handleSubmit(onSubmit)} sx={{ p: 5 }}>
            {/* Header */}

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">
                {infographic ? "Update Infographic" : "Add Infographic"}
              </Typography>

              <Button type="submit" variant="contained" color="primary">
                {infographic ? "Update" : "Save"}
              </Button>
            </Box>

            {/* Description */}

            <Box my={3}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                variant="outlined"
                {...register("description", {
                  required: "Description is required",
                })}
                error={Boolean(errors.description)}
                helperText={errors.description?.message}
              />
            </Box>

            {/* Upload */}

            {/* <Box my={3}>
              <Typography variant="body1" gutterBottom>
                Images
              </Typography>

              <Box
                {...getRootProps()}
                sx={{
                  border: "2px dashed #d0d5dd",
                  borderRadius: 2,
                  minHeight: 180,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  p: 2,
                }}
              >
                <input {...getInputProps()} />

                <Typography>
                  Drag & drop images here or click to browse (or upload)
                </Typography>
              </Box>
            </Box> */}
            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed #d0d5dd",
                borderRadius: 2,
                minHeight: 220,
                p: 2,
                cursor: "pointer",
              }}
            >
              <input {...getInputProps()} />

              {galleryImages.length === 0 ? (
                <Box
                  sx={{
                    height: 180,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography color="text.secondary">
                    Drag & drop images here or click to browse
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  // sx={{ mb: 2 }}
                  >
                    Click anywhere to add more images
                  </Typography>

                  <ImageList cols={3} gap={12}>
                    {galleryImages.map((image, index) => (
                      <ImageListItem
                        key={index}
                        sx={{
                          position: "relative",
                          overflow: "hidden",
                          borderRadius: 2,
                        }}
                      >
                        <img
                          src={
                            image.file instanceof File
                              ? URL.createObjectURL(image.file)
                              : image.file
                          }
                          alt=""
                          style={{
                            aspectRatio: "3 / 4",
                            objectFit: "cover",
                            borderRadius: 8,
                          }}
                        />

                        {/* Edit */}
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            openCropper(image, index);
                          }}
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            bgcolor: "rgba(0,0,0,.6)",
                            color: "#fff",
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>

                        {/* Delete */}
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImage(index);
                          }}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: "rgba(0,0,0,.6)",
                            color: "#fff",
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ImageListItem>
                    ))}
                  </ImageList>
                </>
              )}
            </Box>
            {imageError && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
              >
                {imageError}
              </Typography>
            )}
            {/* Gallery */}
          </FormWrapper>
        </Box>
      </Drawer>

      <ProductImageCropper
        image={selectedImage}
        open={cropperOpen}
        setOpen={setCropperOpen}
        setImage={handleCroppedImage}
      />
    </>
  );
}
